'use client';

import type { AiComponentDto } from '@terra/shared-contracts-ai-applications';
import { shouldOfferConnectSourceCode } from '@terra/shared-utils';
import {
  ApplicationMcpAssets,
  type AssetsHeaderMetricCard,
  type McpComponentAssetRow,
} from '@terra/ui-design-system';
import { Box } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { AI_COMPONENT_TYPE_KEYS } from '../utils/aiComponentTypeLabels';
import {
  AI_COMPONENT_TYPE_ICON_COLORS,
  getAiComponentTypeIcon,
} from '../utils/aiComponentIcons';
import {
  formatMcpIdentityAccessTooltipContent,
  getMcpComponentIdentityAccessSummary,
} from '../utils/aiComponentMetadata';

const buildMcpComponentAssetRow = ({
  component,
  findingsCount,
  signalsCount,
  tCards,
  tComponentTypes,
  tMcp,
}: {
  component: AiComponentDto;
  findingsCount: number;
  signalsCount: number;
  tCards: ReturnType<
    typeof useTranslations<'designSystem.applicationAssets.cards'>
  >;
  tComponentTypes: ReturnType<
    typeof useTranslations<'aiApplications.componentTypes'>
  >;
  tMcp: ReturnType<
    typeof useTranslations<'aiApplications.componentDrawer.mcp'>
  >;
}): McpComponentAssetRow => {
  const Icon = getAiComponentTypeIcon(component.type);
  const iconColors = AI_COMPONENT_TYPE_ICON_COLORS[component.type];
  const hasCodeGraph = Boolean(
    component.codeGraphId ?? component.aiFlowGraphId,
  );
  const identityAccessSummary = getMcpComponentIdentityAccessSummary({
    metadata: component.metadata,
  });

  return {
    id: component.id,
    name: component.name,
    typeLabel: tComponentTypes(AI_COMPONENT_TYPE_KEYS[component.type]),
    iconElement: (
      <Box
        bg={iconColors.bg}
        borderWidth="1px"
        borderColor={iconColors.border}
        color={iconColors.color}
        borderRadius="sm"
        p={1}
        flexShrink={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Icon size={16} />
      </Box>
    ),
    findingsCount,
    signalsCount,
    identitiesCount:
      identityAccessSummary && identityAccessSummary.totalCount >= 2
        ? identityAccessSummary.totalCount
        : 0,
    identityAccessTooltipContent: formatMcpIdentityAccessTooltipContent({
      metadata: component.metadata,
      labels: {
        accessDenied: tMcp('notListed'),
        call: tMcp('accessCall'),
      },
    }),
    hasCodeGraph,
    graphTooltipTitle: hasCodeGraph
      ? tCards('viewCodeGraphTitle')
      : tCards('graphNotGeneratedTitle'),
    graphAriaLabel: hasCodeGraph
      ? tCards('viewCodeGraphAria')
      : tCards('codeGraphPendingAria'),
  };
};

export type AiApplicationMcpAssetsPanelProps = {
  applicationId: string;
  filteredComponents: AiComponentDto[];
  signalCountsByComponentId: Record<string, number>;
  findingCountsByComponentId: Record<string, number>;
  componentDetectionMetrics: {
    hasFindings: number;
    hasSignals: number;
    noDetections: number;
  };
  isLoading?: boolean;
  isSignalsUnavailable?: boolean;
  isFindingsUnavailable?: boolean;
  hasActiveFilters?: boolean;
  isAffectedAssetsChecked?: boolean;
  onClearFilters?: () => void;
  onToggleAffectedAssets?: (checked: boolean) => void;
  onSelectComponent: (componentId: string) => void;
  onViewGraph?: (componentId: string) => void;
  hasSourceCode?: boolean;
  onConnectSourceCode?: () => void;
};

export function AiApplicationMcpAssetsPanel({
  applicationId,
  filteredComponents,
  signalCountsByComponentId,
  findingCountsByComponentId,
  componentDetectionMetrics,
  isLoading = false,
  isSignalsUnavailable = false,
  isFindingsUnavailable = false,
  hasActiveFilters = false,
  isAffectedAssetsChecked = false,
  onClearFilters,
  onToggleAffectedAssets,
  onSelectComponent,
  onViewGraph,
  hasSourceCode = true,
  onConnectSourceCode,
}: AiApplicationMcpAssetsPanelProps) {
  const tCommon = useTranslations('common');
  const tAssets = useTranslations('aiApplications.assets');
  const tCards = useTranslations('designSystem.applicationAssets.cards');
  const tComponentTypes = useTranslations('aiApplications.componentTypes');
  const tMcp = useTranslations('aiApplications.componentDrawer.mcp');

  const mcpComponentAssets = useMemo(
    () =>
      filteredComponents.map((component) =>
        buildMcpComponentAssetRow({
          component,
          findingsCount: findingCountsByComponentId[component.id] ?? 0,
          signalsCount: signalCountsByComponentId[component.id] ?? 0,
          tCards,
          tComponentTypes,
          tMcp,
        }),
      ),
    [
      filteredComponents,
      findingCountsByComponentId,
      signalCountsByComponentId,
      tCards,
      tComponentTypes,
      tMcp,
    ],
  );

  const customMetricCards = useMemo<AssetsHeaderMetricCard[]>(
    () => [
      {
        title: tAssets('hasFindings'),
        value: isFindingsUnavailable
          ? tCommon('na')
          : `${componentDetectionMetrics.hasFindings}`,
        tooltip: tAssets('hasFindingsTooltip'),
      },
      {
        title: tAssets('hasSignals'),
        value: isSignalsUnavailable
          ? tCommon('na')
          : `${componentDetectionMetrics.hasSignals}`,
        tooltip: tAssets('hasSignalsTooltip'),
      },
      {
        title: tAssets('noDetections'),
        value:
          isFindingsUnavailable || isSignalsUnavailable
            ? tCommon('na')
            : `${componentDetectionMetrics.noDetections}`,
        tooltip: tAssets('noDetectionsTooltip'),
      },
    ],
    [
      componentDetectionMetrics,
      isFindingsUnavailable,
      isSignalsUnavailable,
      tAssets,
      tCommon,
    ],
  );

  const codeGraphBannerMode = useMemo(() => {
    if (shouldOfferConnectSourceCode({ hasSourceCode, onConnectSourceCode })) {
      return 'offerConnect' as const;
    }

    if (!hasSourceCode) {
      return undefined;
    }

    if (
      filteredComponents.some(
        (component) => !component.codeGraphId && !component.aiFlowGraphId,
      )
    ) {
      return 'missingGraphs' as const;
    }

    return undefined;
  }, [filteredComponents, hasSourceCode, onConnectSourceCode]);

  return (
    <ApplicationMcpAssets
      applicationId={applicationId}
      mcpComponentAssets={mcpComponentAssets}
      isLoading={isLoading}
      hasActiveFilters={hasActiveFilters}
      onClearFilters={onClearFilters}
      customMetricCards={customMetricCards}
      onMcpComponentSelect={onSelectComponent}
      onMcpComponentViewGraph={onViewGraph}
      isFindingsUnavailable={isFindingsUnavailable}
      isSignalsUnavailable={isSignalsUnavailable}
      codeGraphBannerMode={codeGraphBannerMode}
      onConnectSourceCode={onConnectSourceCode}
      showCodeGraphColumn={hasSourceCode}
      isAffectedAssetsChecked={isAffectedAssetsChecked}
      onToggleAffectedAssets={onToggleAffectedAssets}
    />
  );
}
